import {GitMergeIcon, GitPullRequestClosedIcon} from '@primer/octicons-react'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {useMergeabilityData, type MergeBoxPullRequest} from './use-mergeability-data'
import {Button} from '@primer/react'
import {useDeleteHeadRefMutation} from '@github-ui/mergebox/use-delete-head-ref-mutation'
import {FailureDialog} from '../../sidebar-actions/FailureDialog'
import {BranchLink} from './shared/BranchLink'
import {announce} from '@github-ui/aria-live'
import {useRef} from 'react'

export function DeleteHeadBranch() {
  const data = useMergeabilityData()

  if (!data) {
    return null
  }

  const {mergeBoxPullRequest, isClosedOrMerged} = data

  if (!isClosedOrMerged || !mergeBoxPullRequest.viewerCanDeleteHeadRef) {
    return null
  }

  if (mergeBoxPullRequest.state === 'MERGED') {
    return <DeleteMergedHeadBranch pullRequest={mergeBoxPullRequest} />
  }

  return <DeleteClosedHeadBranch pullRequest={mergeBoxPullRequest} />
}

function DeleteMergedHeadBranch({pullRequest}: {pullRequest: MergeBoxPullRequest}) {
  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={GitMergeIcon} variant="done" />}
      title="Pull request successfully merged and closed"
      itemName="delete-head-branch"
      description={
        <>
          You&apos;re all set &mdash; the{' '}
          <BranchLink refName={pullRequest.headRefName} repository={pullRequest.headRepository} linkInTextBlock />{' '}
          branch can be safely deleted.
        </>
      }
      actionSlot={<DeleteBranchAction />}
    />
  )
}

function DeleteClosedHeadBranch({pullRequest}: {pullRequest: MergeBoxPullRequest}) {
  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={GitPullRequestClosedIcon} variant="default" />}
      title="Closed with unmerged commits"
      itemName="delete-head-branch"
      description={
        <>
          This pull request is closed, but the{' '}
          <BranchLink refName={pullRequest.headRefName} repository={pullRequest.headRepository} linkInTextBlock />{' '}
          branch has unmerged commits.
        </>
      }
      actionSlot={<DeleteBranchAction />}
    />
  )
}

function DeleteBranchAction() {
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const deleteHeadRefMutation = useDeleteHeadRefMutation({
    onSuccess: () => {
      // Move focus before query invalidation removes the action from the DOM.
      findPreviousTabbableElementOutsideRow(deleteButtonRef.current)?.focus()
      announce('Branch deleted')
    },
  })

  return (
    <>
      <Button
        ref={deleteButtonRef}
        loading={deleteHeadRefMutation.isPending}
        loadingAnnouncement="Deleting branch"
        onClick={() => deleteHeadRefMutation.mutate()}
        size="small"
      >
        Delete branch
      </Button>

      {deleteHeadRefMutation.isError && (
        <FailureDialog
          title="Failed to delete branch"
          message="An error occurred while deleting the head branch. Please try again."
          details={deleteHeadRefMutation.error.message}
          onRetry={() => deleteHeadRefMutation.mutate()}
          onDismiss={() => deleteHeadRefMutation.reset()}
        />
      )}
    </>
  )
}

function findPreviousTabbableElementOutsideRow(element: HTMLElement | null) {
  if (!element) return null

  const row = element.closest('li')
  const tabbableElements = Array.from(
    document.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]'),
  ).filter(candidate => candidate.tabIndex >= 0 && !candidate.matches(':disabled') && candidate.checkVisibility())
  const previousTabbableElements = tabbableElements.slice(0, tabbableElements.indexOf(element)).reverse()
  return previousTabbableElements.find(candidate => !row?.contains(candidate)) ?? null
}
